import { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { Field } from '@base-ui/react/field';
import { IconButton } from 'components/Button';
import { MagnifyingGlassIcon, XIcon } from 'components/Icon';
import {
  textInputHelperRecipe,
  textInputIconRecipe,
  textInputPillBoxRecipe,
  textInputPillControlRecipe,
  textInputPillRootRecipe,
  textInputPillTrailingButtonRecipe,
} from 'components/TextInput/TextInput.recipe';
import { cn } from 'components/utils';

import { searchBoxRecipe, searchControlRecipe, searchHelperRecipe, searchRootRecipe } from './Search.recipe';

import type { SearchProps } from './types';

// Base UI's own event type, which carries `preventBaseUIHandler` on top of React's.
type ControlKeyboardEvent = Parameters<NonNullable<SearchProps['onKeyDown']>>[0];

/**
 * Design System 2.0 search field (Figma 1993:125669).
 *
 * The pill with the search contract built in, so no call site re-wires it:
 *
 *   - the leading glyph is always MagnifyingGlass — it is part of the component, not a slot;
 *   - the clear button appears only once there is a value (`isFilled`), clears, and puts the caret
 *     back in the field;
 *   - Escape clears a filled field and stops there, so a surrounding Menu or Popover stays open. On
 *     an empty field Escape is left alone, so the popup closes as it would anyway;
 *   - `helperText` is the result count, announced politely. The empty state is the list's job.
 *
 * There is no error state: a search box never rejects input. Debouncing is the caller's, because a
 * local filter wants none and a server query wants some.
 *
 * `State` and `isFilled` are observed from the DOM through Base UI's `Field`, exactly as on the pill.
 */
export const Search = forwardRef<HTMLInputElement, SearchProps>(
  (
    {
      label,
      placeholder,
      clearLabel,
      value,
      defaultValue,
      onValueChange,
      onClear,
      onKeyDown,
      helperText,
      disabled,
      size: sizeProp,
      placement = 'standalone',
      className,
      rootClassName,
      ...rest
    },
    ref
  ) => {
    const size = placement === 'menu' ? 'small' : (sizeProp ?? 'medium');
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const [uncontrolled, setUncontrolled] = useState(defaultValue ?? '');
    const current = value ?? uncontrolled;

    const change = (next: string) => {
      if (value === undefined) setUncontrolled(next);
      onValueChange?.(next);
    };

    const clear = () => {
      change('');
      onClear?.();
      inputRef.current?.focus();
    };

    const handleKeyDown = (event: ControlKeyboardEvent) => {
      onKeyDown?.(event);
      if (event.defaultPrevented || event.key !== 'Escape' || current === '') return;
      event.preventDefault();
      event.stopPropagation();
      clear();
    };

    return (
      <Field.Root
        className={cn(textInputPillRootRecipe(), searchRootRecipe({ placement }), rootClassName)}
        disabled={disabled}
      >
        <Field.Label className="sr-only">{label}</Field.Label>

        <div className={cn(textInputPillBoxRecipe(), searchBoxRecipe({ size, placement }), className)} data-part="box">
          {/* Figma's `.Input` puts 10px between glyph and text; the box's own 4px gap is the one
              before the clear button, so the glyph adds the other 6. The pill's code has 4 here,
              which is a pill discrepancy left for its own fix. */}
          <span className={cn(textInputIconRecipe(), 'mr-[6px]')} data-part="icon">
            <MagnifyingGlassIcon />
          </span>
          <Field.Control
            ref={inputRef}
            autoComplete="off"
            className={cn(textInputPillControlRecipe(), searchControlRecipe({ size }))}
            placeholder={placeholder ?? label}
            spellCheck={false}
            type="search"
            value={current}
            onKeyDown={handleKeyDown}
            onValueChange={(next) => change(next)}
            {...rest}
          />
          {current !== '' && (
            <IconButton
              appearance="tertiary"
              aria-label={clearLabel}
              className={textInputPillTrailingButtonRecipe()}
              data-part="clear"
              disabled={disabled}
              fill="default"
              onClick={clear}
              size="2xs"
              type="button"
            >
              <XIcon />
            </IconButton>
          )}
        </div>

        {helperText && (
          <Field.Description
            aria-live="polite"
            className={cn(textInputHelperRecipe({ shape: 'pill' }), searchHelperRecipe({ size }))}
          >
            {helperText}
          </Field.Description>
        )}
      </Field.Root>
    );
  }
);
